import { CallsScreen, CallsSkeleton } from "@commerce/calls/CallsScreen";
import { callsSearchSchema, listInputFrom } from "@commerce/calls/meta";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/calls/",
)({
	validateSearch: callsSearchSchema,
	staticData: { fullBleed: true },
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const search = callsSearchSchema.parse(location.search);
		const queryClient = getServerQueryClient();
		// A failed prefetch is not the page failing: the screen's own query retries it.
		await Promise.all([
			queryClient
				.ensureQueryData(
					orpc.calls.list.queryOptions({
						input: listInputFrom(organizationId, search),
					}),
				)
				.catch(() => null),
			queryClient
				.ensureQueryData(
					orpc.calls.live.queryOptions({ input: { organizationId } }),
				)
				.catch(() => null),
			search.id
				? queryClient
						.ensureQueryData(
							orpc.calls.get.queryOptions({
								input: { organizationId, callId: search.id },
							}),
						)
						.catch(() => null)
				: null,
		]);
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	// Shown inside the Shell while the loader runs on a client navigation.
	pendingComponent: CallsSkeleton,
	head: () => ({ meta: [{ title: `Calls - ${config.appName}` }] }),
	component: CallsPage,
});

function CallsPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	return (
		<AsyncBoundary
			fallback={<CallsSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<CallsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
